import { createIcon, type IconAPI } from "@UI/Icon/index.js";
import "./ListItem.scss"; // Co-located styles per repo standards [[memory:8094583]]

export interface ListItemProps {
  readonly id: string;
  readonly leftIcon?: string;
  readonly primaryLabel: string;
  readonly secondaryLabel?: string;
  readonly badges?: readonly string[];
  readonly rightActions?: readonly { icon: string; id: string; title?: string }[];
  readonly selected?: boolean;
  readonly disabled?: boolean;
}

export interface ListItemConfig extends ListItemProps {
  readonly onSelect?: (id: string) => void;
  readonly onAction?: (actionId: string, itemId: string) => void;
  readonly className?: string;
}

export interface ListItemAPI {
  readonly getElement: () => HTMLElement;
  readonly setSelected: (selected: boolean) => void;
  readonly setDisabled: (disabled: boolean) => void;
  readonly update: (props: Partial<ListItemProps>) => void;
  readonly focus: () => void;
  readonly destroy: () => void;
}

export const createListItem = (config: ListItemConfig): ListItemAPI => {
  const {
    id,
    leftIcon,
    primaryLabel,
    secondaryLabel,
    badges = [],
    rightActions = [],
    selected = false,
    disabled = false,
    onSelect,
    onAction,
    className = "",
  } = config;

  let isDestroyed = false;
  let isSelected = selected;
  let isDisabled = disabled;
  let iconAPI: IconAPI | null = null;

  const root = document.createElement("div");
  root.className = `list-item ${className}`.trim();
  root.setAttribute("tabindex", "-1");

  const left = document.createElement("div");
  left.className = "list-item__left";

  const content = document.createElement("div");
  content.className = "list-item__content";

  const labels = document.createElement("div");
  labels.className = "list-item__labels";

  const primary = document.createElement("div");
  primary.className = "list-item__primary";
  primary.textContent = primaryLabel;

  const secondary = document.createElement("div");
  secondary.className = "list-item__secondary";
  if (secondaryLabel) secondary.textContent = secondaryLabel;

  const badgesEl = document.createElement("div");
  badgesEl.className = "list-item__badges";

  const right = document.createElement("div");
  right.className = "list-item__right";

  const renderIcon = (): void => {
    if (!leftIcon) return;
    if (iconAPI) {
      iconAPI.destroy();
      iconAPI = null;
    }
    try {
      iconAPI = createIcon({ name: leftIcon, size: 20, useContainer: true });
      left.appendChild(iconAPI.getElement());
    } catch (e) {
      // Ignore missing icon
    }
  };

  const renderBadges = (values: readonly string[]): void => {
    badgesEl.innerHTML = "";
    values.forEach((b) => {
      const el = document.createElement("span");
      el.className = "list-item__badge";
      el.textContent = b;
      badgesEl.appendChild(el);
    });
  };

  const renderActions = (actions: readonly { icon: string; id: string; title?: string }[]): void => {
    right.innerHTML = "";
    actions.forEach((action) => {
      const btn = document.createElement("button");
      btn.className = "list-item__action";
      btn.type = "button";
      if (action.title) btn.title = action.title;
      try {
        const aIcon = createIcon({ name: action.icon, size: 16, useContainer: true });
        btn.appendChild(aIcon.getElement());
      } catch {}
      btn.addEventListener("click", (e) => {
        e.stopPropagation();
        if (!isDisabled) onAction?.(action.id, id);
      });
      right.appendChild(btn);
    });
  };

  const updateState = (): void => {
    root.classList.toggle("list-item--selected", isSelected);
    root.classList.toggle("list-item--disabled", isDisabled);
  };

  const handleClick = (): void => {
    if (isDisabled) return;
    onSelect?.(id);
  };

  const handleKey = (e: KeyboardEvent): void => {
    if (isDisabled) return;
    if (e.key === "Enter") {
      e.preventDefault();
      onSelect?.(id);
    }
  };

  // Build DOM
  labels.appendChild(primary);
  labels.appendChild(secondary);
  content.appendChild(labels);
  content.appendChild(badgesEl);
  root.appendChild(left);
  root.appendChild(content);
  root.appendChild(right);

  // Initial render
  renderIcon();
  renderBadges(badges);
  renderActions(rightActions);
  updateState();

  root.addEventListener("click", handleClick);
  root.addEventListener("keydown", handleKey);

  const api: ListItemAPI = {
    getElement: () => root,
    setSelected: (selected: boolean) => {
      isSelected = selected;
      updateState();
    },
    setDisabled: (disabled: boolean) => {
      isDisabled = disabled;
      updateState();
    },
    update: (props: Partial<ListItemProps>) => {
      if (props.leftIcon !== undefined) {
        (left.innerHTML = ""), (iconAPI?.destroy(), (iconAPI = null));
        if (props.leftIcon) {
          try {
            iconAPI = createIcon({ name: props.leftIcon, size: 20, useContainer: true });
            left.appendChild(iconAPI.getElement());
          } catch {}
        }
      }
      if (props.primaryLabel !== undefined) primary.textContent = props.primaryLabel ?? "";
      if (props.secondaryLabel !== undefined) secondary.textContent = props.secondaryLabel ?? "";
      if (props.badges !== undefined) renderBadges(props.badges);
      if (props.rightActions !== undefined) renderActions(props.rightActions);
      if (props.selected !== undefined) {
        isSelected = !!props.selected;
      }
      if (props.disabled !== undefined) {
        isDisabled = !!props.disabled;
      }
      updateState();
    },
    focus: () => {
      root.focus();
    },
    destroy: () => {
      if (isDestroyed) return;
      isDestroyed = true;
      root.removeEventListener("click", handleClick);
      root.removeEventListener("keydown", handleKey);
      iconAPI?.destroy();
      iconAPI = null;
      root.remove();
    },
  };

  return api;
};


